import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ChevronLeft, ChevronRight, MapPin, Clock, Users, ArrowLeft, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

export default function CampCalendar() {
  const navigate = useNavigate();
  const [camps, setCamps] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [waitlistForm, setWaitlistForm] = useState({
    participant_name: "",
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    age: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCamps();
  }, []);

  const fetchCamps = async () => {
    try {
      const response = await axios.get(`${API}/calendar/camps`);
      setCamps(response.data);
    } catch (error) {
      console.error("Error fetching camps:", error);
      toast.error("Failed to load camps");
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    if (!selectedCamp) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        camp_id: selectedCamp.id,
        camp_name: selectedCamp.title,
        participant_name: waitlistForm.participant_name,
        parent_name: waitlistForm.parent_name,
        parent_email: waitlistForm.parent_email,
        parent_phone: waitlistForm.parent_phone,
        age: parseInt(waitlistForm.age)
      });
      toast.success("Successfully joined the waitlist! We'll contact you if a spot opens up.");
      setShowWaitlistDialog(false);
      setWaitlistForm({
        participant_name: "",
        parent_name: "",
        parent_email: "",
        parent_phone: "",
        age: ""
      });
    } catch (error) {
      console.error("Error joining waitlist:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to join waitlist");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calendar helpers
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const formatDateString = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getCampsForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return camps.filter(camp => {
      const start = camp.start;
      const end = camp.end;
      return dateStr >= start && dateStr <= end;
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Add day headers
    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={`header-${i}`} className="text-center text-zinc-500 font-medium py-2 text-sm">
          {dayNames[i]}
        </div>
      );
    }

    // Add empty cells for days before the first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    // Add days
    for (let day = 1; day <= daysInMonth; day++) {
      const campsOnDay = getCampsForDate(day);
      const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();

      days.push(
        <div
          key={day}
          className={`min-h-[80px] sm:min-h-[100px] p-1 sm:p-2 border border-white/5 ${isToday ? 'bg-burns-gold/10' : ''}`}
        >
          <span className={`text-xs sm:text-sm ${isToday ? 'text-burns-gold font-bold' : 'text-zinc-400'}`}>
            {day}
          </span>
          <div className="mt-1 space-y-1">
            {campsOnDay.slice(0, 2).map((camp, idx) => (
              <button
                key={camp.id + idx}
                onClick={() => setSelectedCamp(camp)}
                className={`w-full text-left text-[10px] sm:text-xs p-1 rounded truncate ${
                  camp.is_full 
                    ? 'bg-red-900/50 text-red-300 border border-red-500/30' 
                    : 'bg-burns-red/30 text-white hover:bg-burns-red/50'
                }`}
                data-testid={`camp-event-${camp.id}`}
              >
                {camp.title}
              </button>
            ))}
            {campsOnDay.length > 2 && (
              <span className="text-[10px] text-zinc-500">+{campsOnDay.length - 2} more</span>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  return (
    <div className="min-h-screen bg-burns-dark">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-12 w-auto" />
            </Link>
            <Button onClick={() => navigate("/")} variant="ghost" className="text-white hover:text-burns-gold">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto"
        >
          {/* Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-burns-red/20 border border-burns-red/30 px-4 py-2 rounded-sm mb-4">
              <Calendar className="w-5 h-5 text-burns-red" />
              <span className="text-burns-red font-heading tracking-wide">CAMP CALENDAR</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-2">
              UPCOMING <span className="text-burns-gold">CAMPS</span>
            </h1>
            <p className="text-zinc-400">View all scheduled camps and register for upcoming sessions</p>
          </div>

          {/* Calendar Navigation */}
          <div className="flex items-center justify-between mb-6 bg-zinc-950 border border-white/10 rounded-sm p-4">
            <Button onClick={prevMonth} variant="ghost" className="text-white hover:text-burns-gold">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h2 className="font-heading text-xl sm:text-2xl text-white">
              {months[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <Button onClick={nextMonth} variant="ghost" className="text-white hover:text-burns-gold">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-burns-red/30 rounded"></div>
              <span className="text-zinc-400">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-900/50 border border-red-500/30 rounded"></div>
              <span className="text-zinc-400">Full (Waitlist Available)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-burns-gold/10 border border-burns-gold/30 rounded"></div>
              <span className="text-zinc-400">Today</span>
            </div>
          </div>

          {/* Calendar Grid */}
          {isLoading ? (
            <div className="text-center py-20 text-zinc-400">Loading calendar...</div>
          ) : (
            <div className="bg-zinc-950 border border-white/10 rounded-sm overflow-hidden">
              <div className="grid grid-cols-7">
                {renderCalendar()}
              </div>
            </div>
          )}

          {/* Upcoming Camps List */}
          <div className="mt-8">
            <h3 className="font-heading text-xl text-white mb-4">UPCOMING CAMPS</h3>
            <div className="space-y-4">
              {camps.filter(camp => formatDateString(camp.start) >= new Date()).slice(0, 5).map(camp => (
                <div 
                  key={camp.id}
                  className="bg-zinc-950 border border-white/10 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  data-testid={`upcoming-camp-${camp.id}`}
                >
                  <div>
                    <h4 className="text-white font-medium">{camp.title}</h4>
                    <div className="flex flex-wrap gap-4 text-sm text-zinc-400 mt-1">
                      <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {camp.start} to {camp.end}</span>
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {camp.time}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {camp.location}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {camp.is_full ? (
                      <>
                        <span className="text-red-400 text-sm flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" /> Full
                        </span>
                        <Button 
                          onClick={() => { setSelectedCamp(camp); setShowWaitlistDialog(true); }}
                          className="btn-secondary text-sm"
                          data-testid={`waitlist-btn-${camp.id}`}
                        >
                          Join Waitlist
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="text-burns-gold text-sm flex items-center gap-1">
                          <Users className="w-4 h-4" /> {camp.spots_left} spots left
                        </span>
                        <Button 
                          onClick={() => navigate(`/register?camp=${camp.id}`)}
                          className="btn-primary text-sm"
                          data-testid={`register-btn-${camp.id}`}
                        >
                          Register Now
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </main>

      {/* Camp Details Modal */}
      <Dialog open={selectedCamp !== null && !showWaitlistDialog} onOpenChange={() => setSelectedCamp(null)}>
        <DialogContent className="bg-zinc-900 border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-burns-gold">{selectedCamp?.title}</DialogTitle>
          </DialogHeader>
          {selectedCamp && (
            <div className="space-y-4">
              <p className="text-zinc-300">{selectedCamp.description}</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Calendar className="w-4 h-4 text-burns-gold" />
                  <span>{selectedCamp.start} to {selectedCamp.end}</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-400">
                  <Clock className="w-4 h-4 text-burns-gold" />
                  <span>{selectedCamp.time}</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-400">
                  <MapPin className="w-4 h-4 text-burns-gold" />
                  <span>{selectedCamp.location}</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-400">
                  <Users className="w-4 h-4 text-burns-gold" />
                  <span>{selectedCamp.current_participants}/{selectedCamp.max_participants} participants</span>
                </div>
              </div>
              
              <div className="pt-4 border-t border-white/10">
                {selectedCamp.is_full ? (
                  <div className="space-y-3">
                    <p className="text-red-400 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> This camp is currently full
                    </p>
                    <Button 
                      onClick={() => setShowWaitlistDialog(true)}
                      className="btn-secondary w-full"
                    >
                      Join Waitlist
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={() => navigate(`/register?camp=${selectedCamp.id}`)}
                    className="btn-primary w-full"
                  >
                    Register Now ({selectedCamp.spots_left} spots left)
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Waitlist Dialog */}
      <Dialog open={showWaitlistDialog} onOpenChange={setShowWaitlistDialog}>
        <DialogContent className="bg-zinc-900 border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-burns-gold">JOIN WAITLIST</DialogTitle>
          </DialogHeader>
          <p className="text-zinc-400 text-sm">
            Join the waitlist for <strong className="text-white">{selectedCamp?.title}</strong>. 
            We'll contact you if a spot becomes available.
          </p>
          <form onSubmit={handleJoinWaitlist} className="space-y-4">
            <div>
              <Label className="text-zinc-400">Participant Name *</Label>
              <Input
                value={waitlistForm.participant_name}
                onChange={(e) => setWaitlistForm(prev => ({ ...prev, participant_name: e.target.value }))}
                className="bg-transparent border-white/20 text-white"
                placeholder="Child's name"
                required
              />
            </div>
            <div>
              <Label className="text-zinc-400">Age *</Label>
              <Input
                type="number"
                min="5"
                max="18"
                value={waitlistForm.age}
                onChange={(e) => setWaitlistForm(prev => ({ ...prev, age: e.target.value }))}
                className="bg-transparent border-white/20 text-white"
                required
              />
            </div>
            <div>
              <Label className="text-zinc-400">Parent/Guardian Name *</Label>
              <Input
                value={waitlistForm.parent_name}
                onChange={(e) => setWaitlistForm(prev => ({ ...prev, parent_name: e.target.value }))}
                className="bg-transparent border-white/20 text-white"
                required
              />
            </div>
            <div>
              <Label className="text-zinc-400">Parent Email *</Label>
              <Input
                type="email"
                value={waitlistForm.parent_email}
                onChange={(e) => setWaitlistForm(prev => ({ ...prev, parent_email: e.target.value }))}
                className="bg-transparent border-white/20 text-white"
                required
              />
            </div>
            <div>
              <Label className="text-zinc-400">Parent Phone *</Label>
              <Input
                type="tel"
                value={waitlistForm.parent_phone}
                onChange={(e) => setWaitlistForm(prev => ({ ...prev, parent_phone: e.target.value }))}
                className="bg-transparent border-white/20 text-white"
                required
              />
            </div>
            <Button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
              {isSubmitting ? "Joining..." : "JOIN WAITLIST"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
